import React, { useContext, useState, useRef, useEffect } from "react";
import { Add, Remove, Sort } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

import axios from "../../../../../../../../api/axios";
import DataContext from "../../../../../../../../../context/DataContext";
import { useDebouncedEffect } from "../../../../../../../../../hooks/useDebounceEffect";
import useToggleDropdown from "../../../../../../../../../hooks/useToggleDropdown";
import ShowMoreButton from "../../../../../../../../global/ShowMoreButton";
import ActiveFilterIndicator from "./ActiveFilterIndicator";

export default function InnerMultiSelect({
  id,
  title,
  searchTitle,
  selectedItems,
  setSelectedItems,
  setActiveChipLabel,
  checked,
  setChecked,
  isActive,
  nameKey,
  valueKey,
  apiUrl,
  searchKey,
  orderingVal,
  buttonFullW,
  dropdown,
  filters,
  updateFilter,
  showForm,
  setShowForm

}) {
  const { accessToken } = useContext(DataContext);
  const [resLimit, setResLimit] = useState(25);
  const [searchVal, setSearchVal] = useState("");
  const [showMoreVisible, setShowMoreVisible] = useState(true);
  const [options, setOptions] = useState([]);
  const [loadingRes, setLoadingRes] = useState(false);
  const accordionRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  useToggleDropdown(dropdown, accordionRef, () => setShowForm(id, false));

  useDebouncedEffect(
    () => {
      if (!apiUrl || !showForm) {
        return;
      } else {
        setLoadingRes(true);
        let searchQuery = !searchVal
          ? ""
          : `&${!searchKey ? "search" : searchKey}=${searchVal}`;
        axios
          .get(
            `${apiUrl}limit=${resLimit}&ordering=${
              !orderingVal ? "name" : orderingVal
            }${searchQuery}`,
            {
              headers: { Authorization: `Token ${accessToken}` },
            }
          )
          .then((response) => {
            let data = response.data.results;
            if (response.data.count <= resLimit) {
              setShowMoreVisible(false);
            } else {
              setShowMoreVisible(true);
            }
            setOptions(data);
            // create map for indexing pk
            let dataObj = {};
            for (let i = 0; i < data.length; i++) {
              dataObj[data[i][valueKey]] = data[i];
            }
            //compare selectedItems with response data to set checked items
            let initChecked = selectedItems.map((itm) => {
              //Trying to get label data to set Chip label
              setActiveChipLabel(id, dataObj[itm][nameKey]);
              return dataObj[itm];
            });
            

            setChecked(id, initChecked);

            setLoadingRes(false);
          })
          .catch((err) => err);
      }
    },
    [resLimit, searchVal, showForm],
    250
  );

  const handleSelectedItem = (value) => (event) => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    //set internal state
    setChecked(id, newChecked);
    //set parent state
    setSelectedItems(id, newChecked);
    let selections = newChecked.map((item) => item[valueKey]);

    let savedDisplayValues = newChecked.map((item) => item[nameKey]);
    if(savedDisplayValues.length > 0){
      localStorage.setItem(id, savedDisplayValues);
    }else{
      localStorage.removeItem(id);
    }

    //set url query params - if array is empty send null
    updateFilter(id, selections);
  };

  const truncate = (str, max) => {
    if (!str) return;
    return str.length > max ? str.substring(0, max) + "..." : str;
  };

  return (
    <div ref={accordionRef} className="relative w-full bg-white">
      <div className="flex items-center">
        <Button
          onClick={() => setShowForm(id, !showForm)}
          className={`justify-between w-full text-left normal-case text-[16px] p-0 mb-2 ${
            buttonFullW ? "w-full justify-between" : ""
          }`}
        >
          <div className="flex flex-row items-baseline">
            <span className="text-black pr-2">{title}</span>
            <span> {isActive && <ActiveFilterIndicator />}</span>
          </div>

          {!showForm ? (
            <Add className="text-sky-600 mr-2" />
          ) : (
            <Remove className="text-sky-600 mr-2" />
          )}
        </Button>
      </div>
      {showForm && (
         <>
         {" "}
         <TextField
           className={`placeholder:text-center rounded-lg w-full text-sm ${
             dropdown ? "p-2" : ""
           }`}
           placeholder={searchTitle}
           size="small"
           value={searchVal}
           InputLabelProps={{ shrink: true }}
           label={searchTitle}
           onChange={(event) => setSearchVal(event.target.value)}
         />
         <List className="p-0 items-start w-[400px] h-[25vh] overflow-y-auto">
           {checked.length > 0 && (
             <ListItem className="block border-b border-black px-0 mb-4">
               <span className=" py-1 text-[16px] w-full">
                 <Sort />
                 &nbsp; Selected {title}
               </span>
               <List>
                 {checked.map((item) => {
                   const labelId = `checkbox-list-label-${item[valueKey]}`;
                   return (
                     <ListItem
                       key={"selected_" + item[valueKey]}
                       className="py-0"
                     >
                       <ListItemButton
                         role={undefined}
                         onClick={handleSelectedItem(item)}
                         className="py-0"
                       >
                         <ListItemIcon>
                           <Checkbox
                             edge="start"
                             checked={checked.includes(item)}
                             tabIndex={-1}
                             disableRipple
                             inputProps={{ "aria-labelledby": labelId }}
                           />
                         </ListItemIcon>
                         <ListItemText
                           id={labelId}
                           primary={
                             <div>
                               <Typography className="break-all">
                                 {truncate(item[nameKey], 75)}
                               </Typography>
                             </div>
                           }
                         ></ListItemText>
                       </ListItemButton>
                       <div
                         className={`w-[15px] h-[15px] rounded-full ${
                           !item.client && item.entity
                             ? !item.entity.entity_type_label ===
                               "Client Case Management"
                               ? "bg-yellow-400"
                               : "bg-purple-600"
                             : ""
                         }`}
                       />
                     </ListItem>
                   );
                 })}
               </List>
             </ListItem>
           )}
           {options && options.length > 0 ? (
             options.map((item) => {
               const labelId = `checkbox-list-label-${item[valueKey]}`;
               return (
                 <ListItem
                   key={"unselected_" + item[valueKey]}
                   className="py-0"
                 >
                   <ListItemButton
                     role={undefined}
                     onClick={handleSelectedItem(item)}
                     className="py-0"
                   >
                     <ListItemIcon>
                       <Checkbox
                         edge="start"
                         checked={checked.some(checkedItem => checkedItem[valueKey] === item[valueKey])}
                         tabIndex={-1}
                         disableRipple
                         inputProps={{ "aria-labelledby": labelId }}
                       />
                     </ListItemIcon>
                     <ListItemText
                       id={labelId}
                       primary={
                         <div>
                           <Typography className="break-all">
                             {truncate(item[nameKey], 75)}
                           </Typography>
                         </div>
                       }
                     />
                   </ListItemButton>
                   <div
                     className={`w-[15px] h-[15px] rounded-full ${
                       !item.client && item.entity
                         ? !item.entity.entity_type_label ===
                           "Client Case Management"
                           ? "bg-yellow-400"
                           : "bg-purple-600"
                         : ""
                     }`}
                   />
                 </ListItem>
               );
             })
           ) : (
             <ListItem className="p-0">
               <ListItemButton role={undefined} dense>
                 <ListItemText
                   id={"No-Available"}
                   primary={"No Data Available"}
                 />
               </ListItemButton>
             </ListItem>
           )}
         </List>
         <ShowMoreButton
           showMoreVisible={showMoreVisible}
           setResLimit={setResLimit}
           resLimit={resLimit}
           showMoreAmt={25}
         />
       </>
      )}
    </div>
  );
}